import { useEffect, useRef, useState } from 'react';
import { useCiAuthStore } from '../auth/ciAuthStore';
import { useCiPoolStore } from '../authorization/ciPoolStore';
import {
  fetchFilteredActivity,
  isAuthorizationError,
  searchActivityOptions,
} from '../data/ciApi';
import type { ActivitySearchOption, RecentActivityEvent } from '../data/types';
import { RosterMovesNotice } from '../components/RosterMovesNotice';

const PAGE_SIZE = 100;
const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 250;

const EVENT_LABELS: Record<RecentActivityEvent['event_type'], string> = {
  trade: 'Échange',
  signing: 'Signature',
  drop: 'Libération',
  ltir_placed: 'Mise en LTIR',
  ltir_reactivated: 'Réactivation LTIR',
};

const FILTER_TYPES = ['all', 'trade', 'signing', 'drop', 'ltir_placed', 'ltir_reactivated'] as const;

/** Section touchée par un mouvement LTIR (details.isnhl, voir ltirhistory). */
function ltirSectionLabel(event: RecentActivityEvent): string | null {
  const isnhl = event.details?.isnhl;
  if (typeof isnhl !== 'boolean') return null;
  const section = isnhl ? 'principal' : 'réserves';
  if (event.event_type === 'ltir_placed') return `depuis ${section}`;
  if (event.event_type === 'ltir_reactivated') return `vers ${section}`;
  return null;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi} UTC`;
}

export function ActivityFeedPage() {
  const userId = useCiAuthStore((s) => s.user_id);
  const leagueId = useCiPoolStore((s) => s.selectedLeagueId);
  const requestSequence = useRef(0);
  const [events, setEvents] = useState<RecentActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | RecentActivityEvent['event_type']>('all');
  const [poolerQuery, setPoolerQuery] = useState('');
  const [playerQuery, setPlayerQuery] = useState('');
  const [selectedPooler, setSelectedPooler] = useState<ActivitySearchOption | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<ActivitySearchOption | null>(null);
  const [poolerSuggestions, setPoolerSuggestions] = useState<ActivitySearchOption[]>([]);
  const [playerSuggestions, setPlayerSuggestions] = useState<ActivitySearchOption[]>([]);
  const [searchingPoolers, setSearchingPoolers] = useState(false);
  const [searchingPlayers, setSearchingPlayers] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const invalidDateRange = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  useEffect(() => {
    setPoolerQuery('');
    setPlayerQuery('');
    setSelectedPooler(null);
    setSelectedPlayer(null);
    setDateFrom('');
    setDateTo('');
    setFilter('all');
  }, [leagueId]);

  useEffect(() => {
    let cancelled = false;
    const query = poolerQuery.trim();
    if (!userId || !leagueId || selectedPooler || query.length < SEARCH_MIN_LENGTH) {
      setPoolerSuggestions([]);
      setSearchingPoolers(false);
      return;
    }
    setSearchingPoolers(true);
    setSearchError(null);
    const timer = window.setTimeout(() => {
      void searchActivityOptions(userId, leagueId, 'pooler', query).then((result) => {
        if (cancelled) return;
        if (result.error) {
          setPoolerSuggestions([]);
          setSearchError('Impossible de rechercher les pooleurs dans l’historique.');
        } else {
          setPoolerSuggestions(result.data ?? []);
        }
        setSearchingPoolers(false);
      }).catch(() => {
        if (cancelled) return;
        setPoolerSuggestions([]);
        setSearchError('Impossible de rechercher les pooleurs dans l’historique.');
        setSearchingPoolers(false);
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [leagueId, poolerQuery, selectedPooler, userId]);

  useEffect(() => {
    let cancelled = false;
    const query = playerQuery.trim();
    if (!userId || !leagueId || selectedPlayer || query.length < SEARCH_MIN_LENGTH) {
      setPlayerSuggestions([]);
      setSearchingPlayers(false);
      return;
    }
    setSearchingPlayers(true);
    setSearchError(null);
    const timer = window.setTimeout(() => {
      void searchActivityOptions(userId, leagueId, 'player', query).then((result) => {
        if (cancelled) return;
        if (result.error) {
          setPlayerSuggestions([]);
          setSearchError('Impossible de rechercher les joueurs dans l’historique.');
        } else {
          setPlayerSuggestions(result.data ?? []);
        }
        setSearchingPlayers(false);
      }).catch(() => {
        if (cancelled) return;
        setPlayerSuggestions([]);
        setSearchError('Impossible de rechercher les joueurs dans l’historique.');
        setSearchingPlayers(false);
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [leagueId, playerQuery, selectedPlayer, userId]);

  useEffect(() => {
    let cancelled = false;
    const requestId = ++requestSequence.current;
    setLoadingMore(false);
    if (!userId || !leagueId) {
      setLoading(false);
      setEvents([]);
      return;
    }
    if (invalidDateRange) {
      setError(null);
      setLoading(false);
      setEvents([]);
      setHasMore(false);
      return;
    }
    setLoading(true);
    setError(null);
    setEvents([]);
    setHasMore(false);
    void fetchFilteredActivity(userId, leagueId, {
      eventType: filter,
      poolerId: selectedPooler?.id ?? null,
      playerId: selectedPlayer?.id ?? null,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
      limit: PAGE_SIZE,
      offset: 0,
    }).then((result) => {
      if (cancelled) return;
      if (result.error) {
        setError(
          isAuthorizationError(result.error)
            ? 'Accès refusé pour ce pool.'
            : 'Erreur lors du chargement de l’activité.'
        );
      } else {
        const rows = result.data ?? [];
        setEvents(rows);
        setHasMore(rows.length === PAGE_SIZE);
      }
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setError('Erreur lors du chargement de l’activité.');
      setLoading(false);
    });
    return () => {
      cancelled = true;
      if (requestSequence.current === requestId) requestSequence.current += 1;
    };
  }, [dateFrom, dateTo, filter, invalidDateRange, leagueId, selectedPlayer, selectedPooler, userId]);

  const loadMore = async () => {
    if (!userId || !leagueId || loadingMore || invalidDateRange) return;
    const requestId = requestSequence.current;
    setLoadingMore(true);
    try {
      const result = await fetchFilteredActivity(userId, leagueId, {
        eventType: filter,
        poolerId: selectedPooler?.id ?? null,
        playerId: selectedPlayer?.id ?? null,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
        limit: PAGE_SIZE,
        offset: events.length,
      });
      if (requestId !== requestSequence.current) return;
      if (result.error) {
        setError(
          isAuthorizationError(result.error)
            ? 'Accès refusé pour ce pool.'
            : 'Erreur lors du chargement des événements suivants.'
        );
        return;
      }
      const rows = result.data ?? [];
      setEvents((current) => [...current, ...rows]);
      setHasMore(rows.length === PAGE_SIZE);
    } catch {
      if (requestId === requestSequence.current) {
        setError('Erreur lors du chargement des événements suivants.');
      }
    } finally {
      if (requestId === requestSequence.current) setLoadingMore(false);
    }
  };

  const clearFilters = () => {
    setFilter('all');
    setPoolerQuery('');
    setPlayerQuery('');
    setSelectedPooler(null);
    setSelectedPlayer(null);
    setDateFrom('');
    setDateTo('');
    setSearchError(null);
  };

  const hasActiveFilters = filter !== 'all' || selectedPooler || selectedPlayer || dateFrom || dateTo;

  if (!leagueId) {
    return <div className="ci-empty">Sélectionnez un pool pour afficher l&apos;activité.</div>;
  }
  if (loading && events.length === 0) return <div className="ci-empty">Chargement…</div>;
  if (error) return <div className="ci-error">{error}</div>;

  return (
    <div>
      <RosterMovesNotice />
      <section className="ci-section">
        <div className="ci-section__header">
          <h2 className="ci-section__title">Flux d&apos;activité</h2>
          <div className="ci-tabs" role="tablist" aria-label="Filtre par type">
            {FILTER_TYPES.map((eventType) => (
              <button
                key={eventType}
                type="button"
                role="tab"
                aria-selected={filter === eventType}
                className={`ci-tabs__btn${filter === eventType ? ' is-active' : ''}`}
                onClick={() => setFilter(eventType)}
                data-testid={`ci-feed-filter-${eventType}`}
              >
                {eventType === 'all' ? 'Tous' : EVENT_LABELS[eventType]}
              </button>
            ))}
          </div>
        </div>
        <div className="ci-activity-filters" aria-label="Filtres supplémentaires">
          <div className="ci-activity-filter ci-activity-filter--search">
            <label htmlFor="ci-activity-pooler">Pooleur</label>
            <input
              id="ci-activity-pooler"
              className="ci-input"
              type="search"
              autoComplete="off"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={poolerSuggestions.length > 0}
              aria-controls="ci-activity-pooler-options"
              placeholder="Nom du pooleur"
              value={poolerQuery}
              onChange={(event) => {
                setPoolerQuery(event.target.value);
                setSelectedPooler(null);
                setSearchError(null);
              }}
            />
            {poolerQuery.trim().length < SEARCH_MIN_LENGTH && !selectedPooler && (
              <small>Entrez au moins 3 caractères.</small>
            )}
            {searchingPoolers && <small>Recherche dans l’historique…</small>}
            {poolerSuggestions.length > 0 && (
              <ul id="ci-activity-pooler-options" className="ci-activity-suggestions" role="listbox">
                {poolerSuggestions.map((option) => (
                  <li key={option.id} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected="false"
                      onClick={() => {
                        setSelectedPooler(option);
                        setPoolerQuery(option.label);
                        setPoolerSuggestions([]);
                      }}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="ci-activity-filter ci-activity-filter--search">
            <label htmlFor="ci-activity-player">Joueur</label>
            <input
              id="ci-activity-player"
              className="ci-input"
              type="search"
              autoComplete="off"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={playerSuggestions.length > 0}
              aria-controls="ci-activity-player-options"
              placeholder="Nom du joueur"
              value={playerQuery}
              onChange={(event) => {
                setPlayerQuery(event.target.value);
                setSelectedPlayer(null);
                setSearchError(null);
              }}
            />
            {playerQuery.trim().length < SEARCH_MIN_LENGTH && !selectedPlayer && (
              <small>Entrez au moins 3 caractères.</small>
            )}
            {searchingPlayers && <small>Recherche dans l’historique…</small>}
            {playerSuggestions.length > 0 && (
              <ul id="ci-activity-player-options" className="ci-activity-suggestions" role="listbox">
                {playerSuggestions.map((option) => (
                  <li key={option.id} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected="false"
                      onClick={() => {
                        setSelectedPlayer(option);
                        setPlayerQuery(option.label);
                        setPlayerSuggestions([]);
                      }}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="ci-activity-filter">
            <label htmlFor="ci-activity-date-from">Du (UTC)</label>
            <input
              id="ci-activity-date-from"
              className="ci-input"
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
          </div>
          <div className="ci-activity-filter">
            <label htmlFor="ci-activity-date-to">Au (UTC)</label>
            <input
              id="ci-activity-date-to"
              className="ci-input"
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </div>
          {hasActiveFilters && (
            <button type="button" className="ci-btn ci-btn--ghost ci-btn--sm" onClick={clearFilters}>
              Effacer les filtres
            </button>
          )}
        </div>
        {searchError && <p className="ci-error" role="alert">{searchError}</p>}
        {invalidDateRange && (
          <p className="ci-error" role="alert">
            La date « Du » doit être antérieure ou égale à la date « Au ».
          </p>
        )}
        <p className="ci-section__meta" aria-live="polite">
          {hasActiveFilters
            ? `${events.length} événement${events.length === 1 ? '' : 's'} chargé${events.length === 1 ? '' : 's'} pour les filtres sélectionnés.`
            : `Les ${events.length} événements les plus récents sont affichés.`}
          {loading && ' Mise à jour…'}
        </p>

        {events.length === 0 ? (
          <div className="ci-empty">
            {invalidDateRange
              ? 'Aucun événement à afficher pour une plage de dates invalide.'
              : 'Aucun événement à afficher pour ces filtres.'}
          </div>
        ) : (
          <>
            <table className="ci-table" data-testid="ci-activity-feed">
              <thead>
                <tr>
                  <th>Date (UTC)</th>
                  <th>Type</th>
                  <th>Pooleur</th>
                  <th>Joueur</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, index) => (
                  <tr key={`${event.event_type}-${event.event_time}-${event.user_id}-${event.player_id}-${index}`}>
                    <td>{formatDateTime(event.event_time)}</td>
                    <td>
                      {EVENT_LABELS[event.event_type] ?? event.event_type}
                      {ltirSectionLabel(event) && (
                        <span className="ci-activity-detail"> ({ltirSectionLabel(event)})</span>
                      )}
                    </td>
                    <td>{event.username || (event.user_id != null ? `Utilisateur #${event.user_id}` : '—')}</td>
                    <td>{event.player_name || (event.player_id != null ? `Joueur #${event.player_id}` : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div className="ci-activity-load-more">
                <button type="button" className="ci-btn ci-btn--ghost" onClick={() => void loadMore()} disabled={loadingMore}>
                  {loadingMore ? 'Chargement…' : 'Charger plus'}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
